<template>
  <Modal
    :show="summaryModal.isOpen"
    fullscreen
    hide-default-actions
    with-divider
    content-class="summary-picker-modal"
    @close="close"
  >
    <template #title>
      <div>
        Choose job summary
        <div class="text-body-2 mt-1">
          Review the generated options on the right and apply one to the job profile.
          <a
            href="https://help.datapeople.io/article/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </div>
      </div>
    </template>

    <template #content>
      <v-sheet
        class="d-flex ga-8 h-100 overflow-hidden mx-auto"
        max-width="1200"
      >
        <!-- Left column: current summary -->
        <div class="column">
          <div class="text-textDarkest text-subtitle-2 mb-3">
            Current Job Summary
          </div>

          <v-sheet
            class="tabs-placeholder mb-4"
            height="36"
          />

          <div class="column-body pb-8">
            <DpplSafeHtml
              v-if="currentSummaryHtml"
              :html="currentSummaryHtml"
            />
            <p
              v-else
              class="text-body-2 text-textLight"
            >
              No current summary.
            </p>
          </div>
        </div>

        <!-- Right column: generated options -->
        <div class="column">
          <div class="text-textDarkest text-subtitle-2 mb-3">
            Generated Job Summary
          </div>

          <v-tabs
            :model-value="summaryModal.activeTab"
            :disabled="summaryModal.loading"
            density="compact"
            color="primary"
            class="mb-4"
            @update:model-value="onTabChange"
          >
            <v-tab
              v-for="tab in TABS"
              :key="tab.value"
              :value="tab.value"
            >
              {{ tab.label }}

              <template v-if="tab.tooltipKey && getTooltip(tab.tooltipKey).html">
                <DpplTooltip
                  tooltip-placement="bottom"
                  teleport
                >
                  <template #activator="{ onMouseenter, onMouseleave }">
                    <v-icon
                      size="small"
                      class="ml-2"
                      @mouseenter="onMouseenter"
                      @mouseleave="onMouseleave"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <template #tooltip-content>
                    <DpplSafeHtml :html="getTooltip(tab.tooltipKey).html" />
                  </template>
                </DpplTooltip>
              </template>
            </v-tab>
          </v-tabs>

          <div class="column-body pb-8">
            <v-window :model-value="summaryModal.activeTab">
              <v-window-item
                v-for="tab in TABS"
                :key="tab.value"
                :value="tab.value"
              >
                <div
                  v-if="summaryModal.loading"
                  class="d-flex justify-center align-center pa-8"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  />
                </div>

                <template v-else-if="summaryModal.content[tab.value]">
                  <div
                    v-if="summaryModal.showRegenerateButtons"
                    class="d-flex justify-end mb-2"
                  >
                    <v-btn
                      size="small"
                      variant="text"
                      color="primary"
                      @click="regenerate(tab.value)"
                    >
                      Regenerate
                    </v-btn>
                  </div>
                  <p>
                    <DpplSafeHtml :html="summaryModal.content[tab.value]" />
                  </p>
                </template>
              </v-window-item>
            </v-window>
          </div>
        </div>
      </v-sheet>
    </template>

    <template #footer>
      <div class="d-flex justify-end ga-2 pa-5 pb-0 border-t-sm">
        <v-btn
          variant="outlined"
          rounded="lg"
          color="primary"
          :disabled="isActionDisabled"
          @click="append"
        >
          Append to Summary
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          rounded="lg"
          :disabled="isActionDisabled"
          @click="replace"
        >
          Replace Summary
        </v-btn>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import Modal from '@/components/shared/Modal.vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import { getTooltip } from '@/helpers/tooltips';
import { useTooltips } from '@/stores/insights/tooltips';

const TABS = [
  { value: 'more_junior', label: 'Junior' },
  { value: 'default', label: 'Base' },
  { value: 'more_senior', label: 'Senior' },
  { value: 'more_specific', label: 'Contextual', tooltipKey: 'jp-summary-generate-more-specific' },
];

const { loadTooltips } = useTooltips();
const store = useCurrentJobProfile();
const {
  summaryModal,
  loadSummaryTab,
  regenerateSummaryTab,
  applySummaryContent,
  appendSummaryContent,
} = store;

const { editorContents, summaryFieldId } = storeToRefs(store);

const currentSummaryHtml = computed(() => {
  const id = summaryFieldId.value;
  if (!id) return '';
  const field = editorContents.value?.[id] || {};
  return [field.header, field.content, field.footer].filter(Boolean).join('');
});

const isActionDisabled = computed(() => (
  summaryModal.loading || !summaryModal.content[summaryModal.activeTab]
));

const onTabChange = (tab) => {
  summaryModal.activeTab = tab;
  loadSummaryTab(tab);
};

const regenerate = (tab) => regenerateSummaryTab(tab);
const replace = () => applySummaryContent();
const append = () => appendSummaryContent();
const close = () => { summaryModal.isOpen = false; };

onMounted(() => loadTooltips([TABS.find((tab) => tab.tooltipKey)?.tooltipKey]));
</script>

<style lang="less">
.summary-picker-modal {
  & > .v-sheet {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  .modal-content-wrapper {
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;

    .content {
      height: 100%;
      overflow: hidden;
    }
  }
}
</style>

<style scoped lang="less">
.column {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

.column-body {
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
}
</style>
